// BannersPage — admin view for the banners table.
//
// Operators land here from the sidebar entry. Severity chip, audience
// pill, schedule, and lifecycle badge per row. Filter chips at the
// top of the page filter the visible list client-side; the backend
// returns every non-deleted row regardless.

import React, { useMemo, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack as MuiStack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  faPlus,
  faPenToSquare,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Topbar } from 'layouts'
import { ActionDialog, AreYouSure, Button } from 'components'
import { useBanners } from 'services/queries/use-banners'
import { useCustomerGroups } from 'services/queries/use-customer-groups'
import { useDeleteBanner } from 'services/queries/use-delete-banner'
import { BannerT, CustomerGroupT } from 'types/global'
import BannerDialog from './banners/banner-dialog'
import {
  BannerLifecycleStatusT,
  deriveBannerStatus,
  formatAudiencePill,
  severityChipColor,
} from './banners/helpers'

const headerCellSx = {
  py: 1.5,
  px: 2.5,
  bgcolor: 'action.hover',
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const headerTypographySx = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
}

const bodyCellSx = {
  py: 1.75,
  px: 2.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

type LifecycleFilter = 'all' | BannerLifecycleStatusT

const FILTER_OPTIONS: Array<{ value: LifecycleFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'ended', label: 'Ended' },
]

const statusChipColor = (
  status: BannerLifecycleStatusT,
): 'default' | 'primary' | 'success' | 'warning' => {
  if (status === 'active') return 'success'
  if (status === 'scheduled') return 'primary'
  if (status === 'ended') return 'default'
  return 'warning'
}

const BannersPage: React.FC = () => {
  const { data: bannersResponse, isLoading } = useBanners()
  const { data: groupsResponse } = useCustomerGroups()
  const deleteMutation = useDeleteBanner()

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [filter, setFilter] = useState<LifecycleFilter>('all')

  const banners: BannerT[] =
    bannersResponse && bannersResponse.success ? bannersResponse.data : []

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>()
    const list: CustomerGroupT[] =
      groupsResponse && groupsResponse.success ? groupsResponse.data : []
    for (const g of list) map.set(g.id, g.name)
    return map
  }, [groupsResponse])

  const visible = useMemo(() => {
    if (filter === 'all') return banners
    return banners.filter((b) => deriveBannerStatus(b) === filter)
  }, [banners, filter])

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return
    deleteMutation.mutate(pendingDeleteId)
    setPendingDeleteId(null)
  }

  return (
    <>
      <Topbar
        title="Banners"
        actions={
          <ActionDialog
            title="New banner"
            removeActions
            maxWidth="md"
            fullWidth
            content={(close: () => void) => <BannerDialog closeFn={close} />}
            render={(open: () => void) => (
              <Button
                onClick={open}
                icon={<FontAwesomeIcon icon={faPlus} />}
                data-cy="new-banner"
              >
                New banner
              </Button>
            )}
          />
        }
      />

      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}
              >
                All banners
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}
              >
                Scheduled in-app announcements rendered on the customer
                dashboard.
              </Typography>
            </Box>
            <MuiStack direction="row" spacing={1} flexWrap="wrap">
              {FILTER_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  onClick={() => setFilter(opt.value)}
                  color={filter === opt.value ? 'primary' : 'default'}
                  variant={filter === opt.value ? 'filled' : 'outlined'}
                  size="small"
                  data-cy={`banner-filter-${opt.value}`}
                />
              ))}
            </MuiStack>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 240,
                gap: 1.5,
              }}
            >
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Loading banners...
              </Typography>
            </Box>
          ) : visible.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 280,
                gap: 1.5,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}
              >
                {banners.length === 0
                  ? 'No banners yet'
                  : `No ${filter} banners`}
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 420 }}
              >
                {banners.length === 0
                  ? 'Banners are in-app announcements shown to customers on the dashboard. Create one to schedule a release note, outage warning, or feature pulse.'
                  : 'Adjust the filter above to see other banners.'}
              </Typography>
              {banners.length === 0 && (
                <ActionDialog
                  title="New banner"
                  removeActions
                  maxWidth="md"
                  fullWidth
                  content={(close: () => void) => (
                    <BannerDialog closeFn={close} />
                  )}
                  render={(open: () => void) => (
                    <Button
                      onClick={open}
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      data-cy="new-banner-empty"
                    >
                      Create your first banner
                    </Button>
                  )}
                />
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 880 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, width: 110 }}>
                      <Typography sx={headerTypographySx}>Severity</Typography>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Typography sx={headerTypographySx}>Message</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 220 }}>
                      <Typography sx={headerTypographySx}>Audience</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 220 }}>
                      <Typography sx={headerTypographySx}>Schedule</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 110 }}>
                      <Typography sx={headerTypographySx}>Status</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 110 }}>
                      <Typography sx={headerTypographySx}>Actions</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((banner) => {
                    const status = deriveBannerStatus(banner)
                    return (
                      <TableRow
                        key={banner.id}
                        sx={{
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(84, 189, 203, 0.08)'
                                : 'primary.50',
                          },
                        }}
                      >
                        <TableCell sx={bodyCellSx}>
                          <Chip
                            label={banner.severity}
                            size="small"
                            color={severityChipColor(banner.severity)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: 'text.primary',
                              fontWeight: 500,
                            }}
                          >
                            {banner.message_en || banner.message_da}
                          </Typography>
                          {(banner.cta_url_en || banner.cta_url_da) && (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                mt: 0.25,
                              }}
                            >
                              CTA:{' '}
                              {banner.cta_label_en ??
                                banner.cta_label_da ??
                                banner.cta_url_en ??
                                banner.cta_url_da}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography sx={{ fontSize: 13 }}>
                            {formatAudiencePill(banner, groupNameById)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography
                            sx={{ fontSize: 13, color: 'text.secondary' }}
                          >
                            {formatDate(banner.starts_at)}
                            {' — '}
                            {formatDate(banner.ends_at)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Chip
                            label={status}
                            size="small"
                            color={statusChipColor(status)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <ActionDialog
                              title="Edit banner"
                              removeActions
                              maxWidth="md"
                              fullWidth
                              content={(close: () => void) => (
                                <BannerDialog closeFn={close} banner={banner} />
                              )}
                              render={(open: () => void) => (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={open}
                                    data-cy={`edit-banner-${banner.id}`}
                                    aria-label={`Edit banner ${banner.id}`}
                                  >
                                    <FontAwesomeIcon
                                      icon={faPenToSquare}
                                      style={{ fontSize: 14 }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            />
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => setPendingDeleteId(banner.id)}
                                data-cy={`delete-banner-${banner.id}`}
                                aria-label={`Delete banner ${banner.id}`}
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  style={{ fontSize: 14 }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <AreYouSure
        open={pendingDeleteId !== null}
        setOpen={(open) => {
          if (!open) setPendingDeleteId(null)
        }}
        onSubmit={handleConfirmDelete}
        title="Delete banner?"
        content="This soft-deletes the banner — it stops appearing on the customer dashboard immediately, but the row stays in the database for audit."
      />
    </>
  )
}

export { BannersPage }
export default BannersPage
